import { useEffect, useState } from "react";
import { api, fmtIsoDateOnly } from "@/lib/api";
import { Modal, Btn } from "@/components/Shared";
import { AlertTriangle, Lock, CheckCircle2 } from "lucide-react";

// AffectedCalculationsDialog — surfaces which calculations a rate-version
// row is currently driving, so admins can audit the blast radius BEFORE
// confirming a destructive delete.
//
// Renders a 3-state body:
//   1. Loading skeleton while GET /rate-versions/{id}/affected is in flight
//   2. "No downstream impact" when both open & closed counts are zero
//   3. Closed-period warning callout (if any closed-period rows) +
//      a scrollable table of period / merchant / gateway / lob /
//      processed_volume / rate_anchor_date / closed-state
//
// Footer: Cancel + a destructive Delete button. The parent dialog actually
// performs the DELETE on confirm — this component only surfaces the
// preview and yields the user's decision back via `onConfirm`.

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

export default function AffectedCalculationsDialog({ open, onClose, onConfirm, versionId, rateLabel, effectiveFromLabel }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !versionId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: resp } = await api.get(`/rate-versions/${versionId}/affected`);
        if (!cancelled) setData(resp);
      } catch (e) {
        if (!cancelled) setData({ error: e?.response?.data?.detail || "Failed to load affected calculations" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, versionId]);

  if (!open) return null;
  const total = (data?.open_count || 0) + (data?.closed_count || 0);
  const hasClosed = (data?.closed_count || 0) > 0;

  const submit = async () => {
    setBusy(true);
    try { await onConfirm(); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Delete rate version?" testid="affected-dialog" size="md">
      <div className="space-y-4">
        <div className="text-[12.5px] text-[var(--muted)] leading-relaxed">
          Rate <b className="text-[var(--ink)]">{rateLabel}</b> effective <b className="text-[var(--ink)]">{effectiveFromLabel}</b>.
          {" "}If this is the currently-active version, the previously-superseded version will become active again.
        </div>

        {loading && (
          <div className="p-6 text-center text-[12px] text-[var(--muted)] border border-[var(--line)] rounded-sm" data-testid="affected-loading">
            Computing downstream impact…
          </div>
        )}

        {!loading && data?.error && (
          <div className="p-3 text-[12px] bg-red-50 border border-red-200 text-red-900 rounded-sm">
            {data.error}
          </div>
        )}

        {!loading && data && !data.error && total === 0 && (
          <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="affected-zero">
            <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-[12px] text-emerald-900">
              <b>No downstream impact.</b> No volumes currently reference this rate version — safe to delete.
            </div>
          </div>
        )}

        {!loading && data && total > 0 && (
          <>
            <div
              className={`flex items-start gap-2 p-3 rounded-sm border ${
                hasClosed ? "bg-amber-50 border-amber-300" : "bg-[var(--surface-2)] border-[var(--line-2)]"
              }`}
              data-testid="affected-warning"
            >
              {hasClosed ? <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" /> : <Lock size={14} className="text-[var(--muted)] mt-0.5 shrink-0" />}
              <div className="text-[12px] leading-relaxed">
                <b>{total} calculation{total === 1 ? "" : "s"}</b> currently use this rate{hasClosed ? (
                  <>{" "}— including <b className="text-amber-900">{data.closed_count} from closed period{data.closed_count === 1 ? "" : "s"}</b>. Deleting will recompute downstream and may change closed-period totals.</>
                ) : (
                  <>{" "}across {data.open_count} open period row{data.open_count === 1 ? "" : "s"}. Calculations will use whatever version covers each period after the delete.</>
                )}
              </div>
            </div>

            <div className="border border-[var(--line)] rounded-sm overflow-hidden" data-testid="affected-table">
              <div className="max-h-[320px] overflow-y-auto">
                <table className="table-data w-full">
                  <thead className="sticky top-0 bg-[var(--surface)]">
                    <tr>
                      <th>Period</th>
                      <th>Merchant</th>
                      <th>Gateway · LOB</th>
                      <th className="text-right">Volume</th>
                      <th>Anchor</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.affected.map((r, i) => (
                      <tr key={`${r.merchant_id}-${r.period}-${i}`} data-testid={`affected-row-${i}`} className={r.period_closed ? "bg-amber-50/60" : ""}>
                        <td className="num text-[11.5px] font-medium">{r.period}</td>
                        <td className="text-[11.5px]" title={r.merchant_name}>
                          <span className="font-mono text-[10.5px] text-[var(--muted)] mr-1">{r.merchant_id}</span>
                          <span className="truncate inline-block max-w-[120px] align-middle">{r.merchant_name}</span>
                        </td>
                        <td className="text-[11px] text-[var(--muted)]">{r.gateway} · {r.lob}</td>
                        <td className="num text-right text-[11.5px]">{fmtINR(r.processed_volume)}</td>
                        <td className="num text-[11px] text-[var(--muted)]">{fmtIsoDateOnly(r.rate_anchor_date)}</td>
                        <td className="text-[10px]">
                          {r.period_closed && (
                            <span className="inline-flex items-center gap-1 text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider text-[9px]">
                              <Lock size={9} /> Closed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div className="text-[10.5px] text-[var(--muted)] leading-relaxed">
          Note: this only removes the rate-version row — the directory page rate stays at its current value. Calculations after the deletion will use whatever version covers the period.
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose} testid="affected-cancel">Cancel</Btn>
          <Btn
            variant="danger"
            onClick={submit}
            disabled={busy || loading}
            testid="affected-confirm"
          >
            {busy ? "Deleting…" : (hasClosed ? `Delete anyway · ${data.closed_count} closed affected` : "Delete version")}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
